import { yupResolver } from '@hookform/resolvers/yup'
import { QueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { ReactElement } from 'react'

import Button from '~/components/Buttons/Button'
import TextInput from '~/components/Inputs/ReactHookForm/TextInput'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import RequiredInformation from '~/components/RequiredInformation'
import { FORM_ERRORS } from '~/const'
import { keys as userFilesQueryKeys, useCreateUserFileMutation } from '~/queries/user-file'
import { handleGenericError } from '~/utils/error'

interface ModalFormProps {
  directoryId: string
  onCloseModal: VoidFunction
  onSuccess: VoidFunction
}

interface IValues {
  fileId: string
}

const validationSchema = Yup.object().shape({
  fileId: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
})

const UploadFileModalForm = ({ directoryId, onCloseModal, onSuccess }: ModalFormProps): ReactElement => {
  /** Local state */

  const queryClient = new QueryClient()

  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm<IValues>({
    defaultValues: { fileId: '' },
    resolver: yupResolver(validationSchema),
  })

  /** Mutation */

  const { mutateAsync: createUserFileMutation } = useCreateUserFileMutation()

  /** Handlers */

  const handleCloseModal = () => {
    reset()
    onCloseModal()
  }

  const handleFormSubmit = async (values: IValues) => {
    const { fileId } = values

    try {
      await createUserFileMutation({ directory_id: directoryId, file_id: fileId })
      await queryClient.invalidateQueries({ queryKey: userFilesQueryKeys.listByDirectory(directoryId) })
      onSuccess()
      reset()
    } catch (e) {
      handleGenericError(e)
    }
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <ModalBody>
        <RequiredInformation />
        <TextInput {...register('fileId')} autoFocus error={errors.fileId?.message} label="File Id" required />
      </ModalBody>
      <ModalFooter>
        <Button ghost onClick={handleCloseModal}>
          Cancel
        </Button>
        <Button type="submit">Add</Button>
      </ModalFooter>
    </form>
  )
}

interface ModalProps extends ModalFormProps {
  isOpen: boolean
  onCloseModal: VoidFunction
}

const UploadFileModal = ({ isOpen, onCloseModal, ...props }: ModalProps): ReactElement => {
  return (
    <Modal isOpen={isOpen} onCloseModal={onCloseModal}>
      <ModalHeader>Add file by Id</ModalHeader>
      <UploadFileModalForm {...props} onCloseModal={onCloseModal} />
    </Modal>
  )
}

export default UploadFileModal
